import { Injectable, computed, signal } from '@angular/core';
import { BillingProfile } from '../models/workspace-billing-profile.model';
import type { IWorkspaceBillingProfilesStore } from './workspace-billing-profiles-store.interface';

@Injectable({ providedIn: 'root' })
export class WorkspaceBillingProfilesMockStore implements IWorkspaceBillingProfilesStore {
  private readonly _profiles = signal<readonly BillingProfile[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly profiles = this._profiles.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._profiles().length);
  readonly defaultProfile = computed<BillingProfile | null>(
    () => this._profiles().find(p => p.isDefault) ?? null
  );
  readonly hasProfiles = computed(() => this._profiles().length > 0);

  async refresh(): Promise<void> {
    this._loading.set(true);
    await Promise.resolve();
    this._loading.set(false);
  }

  loadBillingProfiles(): void {
    // No-op for mock — profiles are seeded explicitly.
  }

  async addProfile(
    profile: Omit<BillingProfile, 'profileId' | 'ownerUID' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const now = new Date();
    const profileId = `mock-wbp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const created = new BillingProfile({
      ...profile,
      profileId,
      ownerUID: 'mock-workspace',
      createdAt: now,
      updatedAt: now
    });
    this._profiles.set([created, ...this._profiles()]);
    return profileId;
  }

  async updateProfile(
    profileId: string,
    profile: Omit<BillingProfile, 'profileId' | 'ownerUID' | 'createdAt'>
  ): Promise<void> {
    this._profiles.set(
      this._profiles().map(p =>
        p.profileId === profileId
          ? new BillingProfile({ ...p, ...profile, updatedAt: new Date() })
          : p
      )
    );
  }

  async deleteProfile(profileId: string): Promise<void> {
    this._profiles.set(this._profiles().filter(p => p.profileId !== profileId));
  }

  async setAsDefault(profileId: string): Promise<void> {
    this._profiles.set(
      this._profiles().map(p =>
        new BillingProfile({ ...p, isDefault: p.profileId === profileId })
      )
    );
  }

  getProfileById(profileId: string): BillingProfile | null {
    return this._profiles().find(p => p.profileId === profileId) ?? null;
  }
}
